import ApiResponse from "../models/ApiResponse";
import axios from "./axios-instance";
import {getDateFromString} from "../utils/DateTimeUtils";
import Interaction from "../models/Interaction";
import {ContactOption} from "../models/enums/ContactOption";
import InteractionSearchParams from "../models/InteractionSearchParams";
import FileMetadata from "../models/FileMetadata";
import FileUrlRequest from "../models/FileUrlRequest";

export const getRecentInteractionsForClient = async (clientId: number): Promise<ApiResponse<Interaction[]>> => {
    try {
        const response = await axios.get('/interaction/recent/' + clientId);
        return {
            data: response.data.map((interaction: any) => (mapResponseToInteraction(interaction))),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get client interactions: ', err.response?.data || err.message)
        return {
            data: [],
            status: err.status
        };
    }
}

export const getInteractionCountForClient = async (clientId: number): Promise<ApiResponse<number>> => {
    try {
        const response = await axios.get('/interaction/count/' + clientId);
        return {
            data: response.data,
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to retrieve interaction count');
        return {
            data: 0,
            status: err.status
        }
    }
}

export const getOutcomesForContactOptions = async (): Promise<ApiResponse<Map<ContactOption, string[]>>> => {
    try {
        const response = await axios.get('/interaction/outcomes');
        return {
            data: new Map(Object.entries(response.data)) as Map<ContactOption, string[]>,
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to get outcomes list');
        return {
            data: new Map(),
            status: err.status
        }
    }
}

export const saveInteraction = async (interaction: Interaction): Promise<ApiResponse<Interaction>> => {
    try {
        const response = await axios.post('/interaction/add', interaction);
        return {
            data: mapResponseToInteraction(response.data),
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to save interaction');
        return {
            data: null,
            status: err.status
        }
    }
}

export const getInteractionById = async (id: number): Promise<ApiResponse<Interaction>> => {
    try {
        const response = await axios.get('/interaction/' + id);
        return {
            data: mapResponseToInteraction(response.data),
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to fetch interaction: ' + id);
        return {
            data: null,
            status: err.status
        }
    }
}

export const updateInteraction = async (interaction: Interaction): Promise<{ status: number }> => {
    try {
        const response = await axios.post('/interaction/update', interaction);
        return { status: response.status };
    } catch (err: any) {
        console.error('Unable to update interaction', err);
        return { status: err.status };
    }
}

export const deleteInteraction = async (id: number): Promise<{status: number}> => {
    try {
        const response = await axios.post('/interaction/delete/' + id);
        return { status: response.status };
    } catch (err: any) {
        console.error('Unable to delete interaction: ' + id, err);
        return { status: err.status };
    }
}

export const getInteractionList = async (params: InteractionSearchParams):
        Promise<ApiResponse<{ interactions: Interaction[], totalCount: number }>> => {
    try {
        const response = await axios.post('/interaction/page', params);
        const interactionList = response.data.content.map((interaction: any) => (mapResponseToInteraction(interaction)));
        return {
            data: {
                interactions: interactionList as Interaction[],
                totalCount: response.data.totalElements
            },
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to get interactions page: ', err.response?.data || err.message);
        return {
            data: {
                interactions: [],
                totalCount: 0
            },
            status: err.status
        }
    }
}

export const saveInteractionFiles = async (id: number, formData: FormData): Promise<{status: number}> => {
    try {
        const response = await axios.post('/interaction/upload/' + id, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }});
        return { status: response.status };
    } catch (err: any) {
        console.error('Failed to upload files', err);
        return { status: err.status };
    }
}

export const getInteractionFileMetadata = async (interactionId: number): Promise<ApiResponse<FileMetadata []>> => {
    try {
        const response = await axios.get('/interaction/files/metadata/' + interactionId);
        return {
            data: response.data as FileMetadata[],
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to retrieve file metadata', err);
        return {
            data: null,
            status: err.status
        }
    }
}

export const getPresignedUrl = async (fileUrlRequest: FileUrlRequest): Promise<ApiResponse<string>> => {
    try {
        const response = await axios.post('/interaction/files/url', fileUrlRequest);
        return {
            data: response.data,
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to retrieve presigned url', err);
        return {
            data: null,
            status: err.status
        }
    }
}

export const deleteInteractionFile = async (interactionId: number, fileName: string): Promise<{status: number}> => {
    try {
        const response = await axios.post('/interaction/files/delete/' + interactionId, null, {
            params: {
                fileName: fileName
            }
        });
        return {status: response.status};
    } catch (err: any) {
        console.error('Failed to delete file', err);
        return {status: err.status};
    }
}

export const getAllInteractionsForUser = async (userId: number): Promise<ApiResponse<Interaction[]>> => {
    try {
        const response = await axios.get('/interaction/all/user/' + userId);
        const interactionList = response.data.map((interaction: any) => (mapResponseToInteraction(interaction)));
        return {
            data: interactionList as Interaction[],
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to get interactions: ', err.response?.data || err.message);
        return {
            data: [],
            status: err.status
        }
    }
}

const mapResponseToInteraction = (interactionData: any): Interaction => {
    return {
        ...interactionData,
        interactionDate: interactionData.interactionDate ? getDateFromString(interactionData.interactionDate) : null
    }
}